import { useState, useMemo, useCallback, useEffect } from "react";
import { format } from "date-fns";
import useRfiStagesActivityLogs from "./useRfiStagesActivityLogs";
import api from "../config/axiosConfig";

/**
 * Custom hook for RFI Stages Activity Log table logic
 * Handles filtering, pagination, expansion state, and data formatting
 */
export default function useRfiStagesActivityLogTable({ rfiNo, open }) {
  // Filter states
  const [filterActionTypes, setFilterActionTypes] = useState([]);
  const [filterStages, setFilterStages] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filterContributors, setFilterContributors] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [actionTypes, setActionTypes] = useState([]);
  const [contributors, setContributors] = useState([]);
  const [rfiCreatedAt, setRfiCreatedAt] = useState(null);

  // Format dates for API (YYYY-MM-DD)
  const formatDateForAPI = useCallback((date) => {
    if (!date) return null;
    try {
      return format(date, "yyyy-MM-dd");
    } catch {
      return null;
    }
  }, []);

  // Fetch activity logs with filters
  // Only pass filter values when they're not in default state
  const { page, setPage, isLoading, logs, totalPages, totalLogs } =
    useRfiStagesActivityLogs({
      rfiNo,
      open,
      limit: 20,
      actionTypes: filterActionTypes.length > 0 ? filterActionTypes : null,
      stageNumbers: filterStages.length > 0 ? filterStages : null,
      startDate: formatDateForAPI(startDate),
      endDate: formatDateForAPI(endDate),
      performedByUsers:
        filterContributors.length > 0 ? filterContributors : null,
    });

  // Fetch action types from API when modal opens
  useEffect(() => {
    const fetchActionTypes = async () => {
      if (!open) return;

      try {
        const response = await api.get(
          "/api/rfi-stages-activity-logs/action-types"
        );
        const allActionTypes = response.data?.data?.all_action_types || [];
        setActionTypes(allActionTypes);
      } catch (error) {
        console.error("Failed to fetch action types:", error);
        setActionTypes([]);
      }
    };

    fetchActionTypes();
  }, [open]);

  // Fetch contributors from API when modal opens
  useEffect(() => {
    const fetchContributors = async () => {
      if (!open || !rfiNo) return;

      try {
        const response = await api.get(
          `/api/rfi-stages-activity-logs/rfi/${rfiNo}/contributors`
        );
        const data = response.data || {};
        const contributorsList = data.contributors || [];
        const rfiInfo = data.rfi_info || {};
        setContributors(contributorsList);

        // Set RFI created date as minimum date for date pickers
        if (rfiInfo.created_at) {
          const createdDate = new Date(rfiInfo.created_at);
          // Set to start of day to avoid time comparison issues
          createdDate.setHours(0, 0, 0, 0);
          setRfiCreatedAt(createdDate);
        }
      } catch (error) {
        console.error("Failed to fetch contributors:", error);
        setContributors([]);
        setRfiCreatedAt(null);
      }
    };

    fetchContributors();
  }, [open, rfiNo]);

  // Toggle row expansion
  const handleToggleExpand = useCallback((logId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [logId]: !prev[logId],
    }));
  }, []);

  // Clear all filters
  const handleClearAllFilters = useCallback(() => {
    setFilterActionTypes([]);
    setFilterStages([]);
    setStartDate(null);
    setEndDate(null);
    setFilterContributors([]);
  }, []);

  // Stage filter options
  const stages = useMemo(
    () => [
      "Stage_1",
      "Stage_2",
      "Stage_3",
      "Stage_4",
      "Stage_5",
      "Stage_6",
      "Stage_7",
    ],
    []
  );

  // Page change handler
  const handlePageChange = useCallback(
    (event, newPage) => {
      setPage(newPage);
    },
    [setPage]
  );

  // Check if row has expandable details
  const hasExpandableDetails = useCallback((log) => {
    return !!(
      log.old_value ||
      log.new_value ||
      log.metadata ||
      log.document_url ||
      log.current_step ||
      log.ip_address
    );
  }, []);

  // Check if row is expanded
  const isRowExpanded = useCallback(
    (logId) => expandedRows[logId] || false,
    [expandedRows]
  );

  return {
    // Filter states
    filterActionTypes,
    setFilterActionTypes,
    filterStages,
    setFilterStages,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    filterContributors,
    setFilterContributors,

    // Data
    logs,
    isLoading,
    page,
    totalPages,
    totalLogs,
    actionTypes,
    stages,
    contributors,
    rfiCreatedAt,

    // Handlers
    handleToggleExpand,
    handleClearAllFilters,
    handlePageChange,

    // Utilities
    hasExpandableDetails,
    isRowExpanded,
  };
}
